// This file is generated by src/design-tokens/scripts/generate-token-outputs.mjs.
// Edit src/design-tokens/source/tokens.source.json, then run npm run tokens:generate.

import colors from './colors.mjs'

const PALETTE_GAPS = {
  /** Status/Warning/Surface - Figma value; palette yellow000 is #FDF1DC. */
  warningBackgroundSurface: '#FFF6E5',
  /** Status/Warning/Icon & Text - Figma value between yellow500 and yellow600. */
  warningIcon: '#EB9B11',
  /** Status/Danger/Text - Figma value between red400 and red500. */
  dangerText: '#C5111B',
  /** Action/Destructive/Hover - Figma value near red100. */
  destructiveHover: '#E6363F',
  /** Status/Danger/Border - Figma value between red000 and red100. */
  dangerBorder: '#F25760',
} as const

export const tokens = {
  color: {
    action: {
      primary: {
        surface: colors.blue.blue600, // #0071E8
        text: colors.neutral.neutral100, // #F9F9F9
        hover: colors.blue.blue500, // #3799FF
        active: colors.blue.blue700, // #0054AD
        disabled: {
          surface: colors.neutral.neutral300, // #D9D9D9
          text: colors.neutral.neutral600, // #979797
          border: colors.neutral.neutral200, // #E5E5E5
        },
        destructive: {
          surface: colors.red.red400, // #DA1E28
          hover: PALETTE_GAPS.destructiveHover, // #E6363F
          active: PALETTE_GAPS.dangerText, // #C5111B
          border: colors.red.red400, // #DA1E28
          text: colors.shades.white, // #ffffff
        },
      },
      // ADX override, see scripts/sync-wasabi/config.json: ADX keeps its blue secondary button
      // until the full merchant-dashboard token adoption lands in the next phase.
      secondary: {
        surface: colors.shades.white, // #ffffff
        text: colors.blue.blue600, // #0071E8
        border: colors.blue.blue600, // #0071E8
        hover: colors.blue.blue100, // #E8F3FF
        active: colors.blue.blue200, // #C1DFFF
        disabled: {
          // Opaque white so the button reads as disabled on tinted backgrounds
          // (e.g. env-banner stripe) instead of letting the parent color bleed through.
          surface: colors.shades.white, // #ffffff
          text: colors.neutral.neutral600, // #979797
          border: colors.neutral.neutral300, // #D9D9D9
        },
      },
      tertiary: {
        text: colors.blue.blue600, // #0071E8
        surface: colors.shades.transparent, // #ffffff00
        hover: 'rgba(0, 113, 232, 0.1)',
        active: 'rgba(0, 113, 232, 0.2)',
        disabled: {
          text: colors.neutral.neutral600, // #979797
        },
        surface_alt: colors.neutral.neutral100, // #F9F9F9
      },
      utility: {
        text: colors.neutral.neutral1000, // #242424
        surface: colors.neutral.neutral100, // #F9F9F9
        border: colors.neutral.neutral400, // #BEBEBE
        hover: colors.neutral.neutral200, // #E5E5E5
        active: colors.neutral.neutral300, // #D9D9D9
        disabled: {
          surface: colors.neutral.neutral300, // #D9D9D9
          text: colors.neutral.neutral600, // #979797
          border: colors.neutral.neutral300, // #D9D9D9
        },
      },
      destructive: {
        surface: colors.red.red400, // #DA1E28
        text: colors.neutral.neutral100, // #F9F9F9
        hover: PALETTE_GAPS.destructiveHover, // #E6363F
        active: colors.red.red500, // #C91C25
      },
    },
    content: {
      text: {
        primary: colors.neutral.neutral1000, // #242424
        secondary: colors.neutral.neutral900, // #494949
        muted: colors.neutral.neutral700, // #767676
        inverse: colors.neutral.neutral100, // #F9F9F9
        disabled: colors.neutral.neutral800, // #666666
        link: colors.blue.blue600, // #0071E8
        muted2: colors.neutral.neutral600, // #979797
      },
      icons: {
        primary: colors.neutral.neutral1000, // #242424
        secondary: colors.neutral.neutral800, // #666666
        muted: colors.neutral.neutral600, // #979797
        disabled: colors.neutral.neutral500, // #ABABAB
        inverse: colors.neutral.neutral100, // #F9F9F9
      },
    },
    status: {
      danger: {
        icon: colors.red.red400, // #DA1E28
        surface: colors.red.red000, // #FCE8E9
        text: PALETTE_GAPS.dangerText, // #C5111B
        border: PALETTE_GAPS.dangerBorder, // #F25760
      },
      warning: {
        icon: PALETTE_GAPS.warningIcon, // #EB9B11
        surface: PALETTE_GAPS.warningBackgroundSurface, // #FFF6E5
        text: PALETTE_GAPS.warningIcon, // #EB9B11
        border: colors.yellow.yellow100, // #F9CC7E
      },
      info: {
        icon: colors.blue.blue600, // #0071E8
        surface: colors.blue.blue100, // #E8F3FF
        text: colors.blue.blue600, // #0071E8
        border: colors.blue.blue400, // #72B7FF
      },
      success: {
        surface: colors.green.green000, // #DBF8E4
        text: colors.green.green400, // #1C903F
        icon: colors.green.green400, // #1C903F
        border: colors.green.green100, // #26C255
      },
      neutral: {
        surface: colors.neutral.neutral100, // #F9F9F9
        surface_alt: colors.neutral.neutral200, // #E5E5E5
        text: colors.neutral.neutral1000, // #242424
        icon: colors.neutral.neutral1000, // #242424
        surface_muted: colors.neutral.neutral400, // #BEBEBE
        border: colors.neutral.neutral400, // #BEBEBE
      },
    },
    background: {
      surface: {
        default: colors.shades.white, // #ffffff
        secondary: colors.neutral.neutral100, // #F9F9F9
        muted: colors.neutral.neutral200, // #E5E5E5
        inverse: colors.neutral.neutral1000, // #242424
        hover: 'rgba(0, 113, 232, 0.05)',
        pressed: 'rgba(0, 113, 232, 0.1)',
      },
      canvas: colors.neutral.neutral100, // #F9F9F9
      overlay: {
        scrim: 'rgba(0, 0, 0, 0.5)',
      },
    },
    input: {
      text: {
        placeholder: colors.neutral.neutral500, // #ABABAB
        value: colors.neutral.neutral1000, // #242424
        label: colors.neutral.neutral900, // #494949
        description: colors.neutral.neutral800, // #666666
        error: colors.red.red400, // #DA1E28
        disabled: colors.neutral.neutral600, // #979797
      },
      surface: {
        default: colors.shades.white, // #ffffff
        hover: colors.neutral.neutral100, // #F9F9F9
        error: colors.shades.white, // #ffffff
        disabled: colors.neutral.neutral300, // #D9D9D9
        dropzone: {
          default: colors.blue.blue100, // #E8F3FF
          hover: colors.blue.blue200, // #C1DFFF
          error: colors.red.red000, // #FCE8E9
        },
      },
      border: {
        default: colors.neutral.neutral400, // #BEBEBE
        error: colors.red.red400, // #DA1E28
        focus: colors.blue.blue600, // #0071E8
        disabled: colors.neutral.neutral400, // #BEBEBE
        hover: colors.neutral.neutral500, // #ABABAB
      },
      icon: {
        default: colors.neutral.neutral800, // #666666
        error: colors.red.red400, // #DA1E28
        info: colors.neutral.neutral1000, // #242424
        interactive: colors.blue.blue600, // #0071E8
        disabled: colors.neutral.neutral600, // #979797
      },
    },
    navigation: {
      text: {
        selected: colors.blue.blue600, // #0071E8
        default: colors.neutral.neutral700, // #767676
        hover: colors.neutral.neutral800, // #666666
      },
      indicator: {
        selected: colors.blue.blue600, // #0071E8
        default: colors.shades.transparent, // #ffffff00
      },
      icon: {
        surface: colors.blue.blue600, // #0071E8
        text: colors.shades.white, // #ffffff
      },
      track: {
        border: colors.neutral.neutral300, // #D9D9D9
      },
    },
    control: {
      background: {
        primary: colors.shades.white, // #ffffff
        secondary: colors.neutral.neutral100, // #F9F9F9
        muted: colors.neutral.neutral500, // #ABABAB
        enabled: colors.blue.blue600, // #0071E8
        hover: colors.neutral.neutral200, // #E5E5E5
        selected: 'rgba(0, 113, 232, 0.1)',
        disabled: {
          icon: colors.neutral.neutral600, // #979797
          surface: colors.neutral.neutral400, // #BEBEBE
        },
      },
      text: {
        inverse: colors.shades.white, // #ffffff
        secondary: colors.neutral.neutral900, // #494949
        primary: colors.neutral.neutral1000, // #242424
        muted: colors.neutral.neutral800, // #666666
      },
      border: {
        default: colors.neutral.neutral1000, // #242424
        selected: colors.blue.blue600, // #0071E8
        disabled: colors.neutral.neutral600, // #979797
      },
      icon: {
        primary: colors.shades.white, // #ffffff
        muted: colors.neutral.neutral300, // #D9D9D9
        surface: {
          primary: colors.blue.blue600, // #0071E8
          muted: colors.neutral.neutral400, // #BEBEBE
        },
      },
    },
    border: {
      default: colors.neutral.neutral400, // #BEBEBE
      subtle: colors.neutral.neutral200, // #E5E5E5
      strong: colors.neutral.neutral600, // #979797
      disabled: colors.neutral.neutral700, // #767676
      inverse: colors.neutral.neutral600, // #979797
      brand: colors.blue.blue600, // #0071E8
      error: colors.red.red400, // #DA1E28
    },
    data: {
      performance: {
        critical: colors.red.red400, // #DA1E28
        poor: colors.coral.coral600, // #E76B35
        concerning: colors.coral.coral400, // #EB8558
        neutral: colors.yellow.yellow400, // #F6B646
        satisfactory: colors.yellow.yellow600, // #F4A721
        good: colors.green.green100, // #26C255
        excellent: colors.green.green400, // #1C903F
        null: colors.neutral.neutral400, // #BEBEBE
      },
      categorical: {
        '1': '#8B5CF6', // #8B5CF6
        '2': '#10B981', // #10B981
        '3': '#F87171', // #F87171
        '4': '#4682D8', // #4682D8
        '5': '#F59E0B', // #F59E0B
        '6': '#14B8A6', // #14B8A6
        '7': '#F472B6', // #F472B6
        '8': '#6366F1', // #6366F1
        '9': '#F97316', // #F97316
        '10': '#C253D3', // #C253D3
        null: colors.neutral.neutral400, // #BEBEBE
      },
    },
    focus: {
      ring: 'rgba(0, 113, 232, 0.3)',
      ringDestructive: colors.red.red000, // #FCE8E9
    },
    progress: {
      indicator: colors.blue.blue600, // #0071E8
      track: colors.neutral.neutral300, // #D9D9D9
      success: colors.green.green200, // #23B14E
    },
  },

  spacing: {
    inset: {
      container: {
        extraTight: '8px',
        tight: '16px',
        default: '24px',
        loose: '32px',
      },
      control: {
        horizontal: {
          tight: '8px',
          default: '16px',
          loose: '24px',
        },
        vertical: {
          tight: '4px',
          default: '8px',
          extraLoose: '16px',
          loose: '12px',
        },
      },
    },
    gap: {
      icon: '4px',
      small: '8px',
      medium: '16px',
      large: '24px',
      extraLarge: '32px',
      section: '48px',
      empty: '64px',
    },
    screen: {
      mobile: '16px',
      desktop: '32px',
    },
    page: {
      width: {
        full: '1200px',
        comfort: '960px',
        focused: '800px',
      },
    },
    control: {
      height: {
        small: '28px',
        medium: '36px',
        large: '40px',
      },
    },
  },

  borderRadius: {
    control: {
      small: '4px',
      medium: '8px',
      full: '9999px',
      sharp: '2px',
    },
    layer: {
      small: '8px',
      medium: '12px',
      large: '16px',
      extraLarge: '24px',
      none: '0px',
    },
  },

  motion: {
    navigation: {
      fullscreenMenu: {
        surface: {
          enterDuration: '160ms',
          exitDuration: '120ms',
        },
        item: {
          duration: '180ms',
          stagger: '20ms',
          maxStaggerSpan: '240ms',
        },
        easing: 'cubic-bezier(0.2, 0, 0, 1)',
      },
    },
    loading: {
      skeleton: {
        duration: '1600ms',
        delay: '150ms',
        easing: 'linear',
      },
      transition: {
        duration: '120ms',
        easing: 'cubic-bezier(0.2, 0, 0, 1)',
      },
    },
    overlay: {
      sheet: {
        surface: {
          enterDuration: '400ms',
          exitDuration: '250ms',
        },
        scrim: {
          enterDuration: '400ms',
          exitDuration: '250ms',
        },
        easing: 'cubic-bezier(0.32, 0.72, 0, 1)',
      },
    },
    layout: {
      sidecar: {
        duration: '260ms',
        easing: 'cubic-bezier(0.2, 0, 0, 1)',
      },
    },
  },

  elevation: {
    card: '0 4px 10px 0 rgba(53, 53, 53, 0.15)',
    illustration: '0 4px 8px rgba(0, 0, 0, 0.15)',
  },
} as const

export const TYPOGRAPHY_MAP = {
  // Headings
  'Heading/Bold/Large': 'heading-bold-large', // 24/700/30
  'Heading/Bold/Medium': 'heading-bold-medium', // 20/700/28
  'Heading/Bold/Small': 'heading-bold-small', // 18/700/26
  'Heading/Bold/ExtraSmall': 'heading-bold-extrasmall', // 16/700/24

  // Body
  'Body/Regular/Large': 'body-regular-large', // 16/400/24
  'Body/Regular/Default': 'body-regular-default', // 14/400/20
  'Body/Regular/Small': 'body-regular-small', // 12/400/18

  // Labels - Regular
  'Label/Regular/Large': 'label-regular-large', // 16/400/24
  'Label/Regular/Medium': 'label-regular-medium', // 14/400/20
  'Label/Regular/Small': 'label-regular-small', // 12/400/18

  // Labels - SemiBold
  'Label/SemiBold/Large': 'label-semibold-large', // 16/600/24
  'Label/SemiBold/Medium': 'label-semibold-medium', // 14/600/20
  'Label/SemiBold/Small': 'label-semibold-small', // 12/600/18
  'Label/SemiBold/ExtraSmall': 'label-semibold-extrasmall', // 10/600/16

  // Metrics - Bold
  'Metric/Bold/Display': 'metric-bold-display', // 64/700/64
  'Metric/Bold/5XL': 'metric-bold-5xl', // 56/700/56
  'Metric/Bold/4XL': 'metric-bold-4xl', // 48/700/48
  'Metric/Bold/3XL': 'metric-bold-3xl', // 40/700/40
  'Metric/Bold/2XL': 'metric-bold-2xl', // 32/700/32
  'Metric/Bold/XL': 'metric-bold-xl', // 24/700/24
  'Metric/Bold/Large': 'metric-bold-large', // 16/700/24
  'Metric/Bold/Medium': 'metric-bold-medium', // 14/700/20
  'Metric/Bold/Small': 'metric-bold-small', // 12/700/18

  // Metrics - Regular
  'Metric/Regular/XL': 'metric-regular-xl', // 24/400/24
  'Metric/Regular/Large': 'metric-regular-large', // 16/400/16
  'Metric/Regular/Medium': 'metric-regular-medium', // 14/400/20
  'Metric/Regular/Small': 'metric-regular-small', // 12/400/18

  // Actions
  'Action/SemiBold/Large': 'action-semibold-large', // 16/600/24
  'Action/SemiBold/Medium': 'action-semibold-medium', // 14/600/20
  'Action/SemiBold/Small': 'action-semibold-small', // 12/600/18
} as const

const { categorical } = tokens.color.data
export const CATEGORICAL_CHART_COLORS = {
  categorical1: categorical[1],
  categorical2: categorical[2],
  categorical3: categorical[3],
  categorical4: categorical[4],
  categorical5: categorical[5],
  categorical6: categorical[6],
  categorical7: categorical[7],
  categorical8: categorical[8],
  categorical9: categorical[9],
  categorical10: categorical[10],
} as const

export type DesignTokens = typeof tokens
export type ColorTokens = typeof tokens.color
export type SpacingTokens = typeof tokens.spacing
export type BorderRadiusTokens = typeof tokens.borderRadius
export type MotionTokens = typeof tokens.motion
export type ElevationTokens = typeof tokens.elevation
